import { Col } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BankIntegrationProvider } from '../../../../api/types';
import SelectField from '../../../../components/fields/SelectField';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import BmwFsBankIntegration from './BmwFsBankIntegration';
import DbsBankIntegration from './DbsBankIntegration';
import EmailBankIntegration from './EmailBankIntegration';
import EnbdBankIntegration from './EnbdBankIntegration';
import HlfBankIntegration from './HlfBankIntegration';
import MaybankIntegration from './MaybankIntegration';
import UobBankIntegration from './UobBankIntegration';

export type BankIntegrationFieldsProps = {
    isUpdate: boolean;
    provider: BankIntegrationProvider;
    compact?: boolean;
    nested: boolean;
    disabled?: boolean;
};

const DEFAULT_COL_SPAN = { lg: 8, md: 12, xs: 24 };
const COMPACT_COL_SPAN = { lg: 24, md: 24, sm: 24, xs: 24 };

const BankIntegrationFields = ({
    isUpdate,
    provider,
    compact = false,
    nested,
    disabled = false,
}: BankIntegrationFieldsProps) => {
    const { t } = useTranslation(['bankDetails']);

    const { bankIntegrationProviderOptions } = useSystemOptions();
    const colSpan = useMemo(() => (compact ? COMPACT_COL_SPAN : DEFAULT_COL_SPAN), [compact]);

    return (
        <>
            {!isUpdate && (
                <Col {...colSpan}>
                    <SelectField
                        {...t('bankDetails:fields.integrationProvider', { returnObjects: true })}
                        disabled={disabled}
                        name="integrationProvider"
                        options={bankIntegrationProviderOptions}
                        required
                        showSearch
                    />
                </Col>
            )}

            {provider === BankIntegrationProvider.Email && (
                <EmailBankIntegration
                    colSpan={colSpan}
                    disabled={disabled}
                    prefix={nested && 'integrationSettings.email'}
                />
            )}

            {provider === BankIntegrationProvider.Hlf && (
                <HlfBankIntegration
                    colSpan={colSpan}
                    disabled={disabled}
                    prefix={nested && 'integrationSettings.hlf'}
                    version="v1"
                />
            )}

            {provider === BankIntegrationProvider.Hlfv2 && (
                <HlfBankIntegration
                    colSpan={colSpan}
                    disabled={disabled}
                    prefix={nested && 'integrationSettings.hlfV2'}
                    version="v2"
                />
            )}

            {provider === BankIntegrationProvider.Uob && (
                <UobBankIntegration
                    colSpan={colSpan}
                    disabled={disabled}
                    prefix={nested && 'integrationSettings.uob'}
                />
            )}

            {provider === BankIntegrationProvider.Dbs && (
                <DbsBankIntegration
                    colSpan={colSpan}
                    disabled={disabled}
                    prefix={nested && 'integrationSettings.dbs'}
                />
            )}

            {provider === BankIntegrationProvider.BmwFs && (
                <BmwFsBankIntegration
                    colSpan={colSpan}
                    disabled={disabled}
                    isUpdate={isUpdate}
                    prefix={nested && 'integrationSettings.bmwFs'}
                />
            )}

            {provider === BankIntegrationProvider.Maybank && (
                <MaybankIntegration
                    colSpan={colSpan}
                    disabled={disabled}
                    isUpdate={isUpdate}
                    prefix={nested && 'integrationSettings.maybank'}
                />
            )}

            {provider === BankIntegrationProvider.Enbd && (
                <EnbdBankIntegration
                    colSpan={colSpan}
                    disabled={disabled}
                    prefix={nested && 'integrationSettings.enbd'}
                />
            )}
        </>
    );
};

export default BankIntegrationFields;
